/**
 * Section: Tw timeline
 * -----------------------------------------------------------------------------
 * Reveals the steps one by one while scrolling. Only classes are toggled, CSS
 * handles the styling. Two modes, from the section's "Start animation" setting
 * (data-animate-start):
 * - view: each step is revealed as it scrolls up past the bottom quarter of
 *   the viewport, and hidden again when it scrolls back below it, so the
 *   animation plays both ways every time.
 * - sticky: nothing moves until the image sticks, then the steps are revealed
 *   in order across the scroll while it stays stuck, and hidden again when
 *   scrolling back up. Below 768px, or without an image, it works like view.
 * Only runs when the "Animate steps on scroll" setting adds data-animate, so a
 * static timeline on the same page is left alone.
 * Wrapped in a guard so a second copy of the section doesn't redefine it.
 *
 */

if (!customElements.get('tw-timeline-progress')) {

  /**
   * Custom element wrapping the timeline list.
   */
  class TwTimelineProgress extends HTMLElement {

    /**
     * Cache the steps and start the chosen mode.
     */
    connectedCallback() {
      if (!this.hasAttribute('data-animate')) {
        return
      }

      this.items = [...this.querySelectorAll('[js-timeline="item"]')]

      if (!this.items.length) {
        return
      }

      this.handleScroll = () => {
        if (this.frame) {
          return
        }

        this.frame = requestAnimationFrame(() => {
          this.frame = null
          this.update()
        })
      }

      if (this.dataset.animateStart === 'sticky') {
        const section = this.closest('.tw-timeline')

        this.container = section?.querySelector('.tw-timeline__container')
        this.media = section?.querySelector('.tw-timeline__media')
        this.desktop = window.matchMedia('(min-width: 768px)')
      }

      window.addEventListener('scroll', this.handleScroll, { passive: true })
      window.addEventListener('resize', this.handleScroll)
      this.update()
    }

    /**
     * Stop listening when the section is removed in the theme editor.
     */
    disconnectedCallback() {
      if (this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll)
        window.removeEventListener('resize', this.handleScroll)
      }
    }

    /**
     * True when the steps follow the sticky image rather than the viewport.
     * @returns {boolean} Whether sticky mode is active.
     */
    isSticky() {
      return Boolean(
        this.media &&
        this.container &&
        this.desktop.matches &&
        window.getComputedStyle(this.media).position === 'sticky',
      )
    }

    /**
     * Work out the current step for the scroll position and show it.
     */
    update() {
      this.setCurrent(this.isSticky() ? this.stickyIndex() : this.viewIndex())
    }

    /**
     * View mode: the last step whose top has passed the bottom quarter of the
     * viewport. The first step always shows.
     * @returns {number} Current step index.
     */
    viewIndex() {
      const line = window.innerHeight * 0.75
      let current = 0

      this.items.forEach((item, index) => {
        if (item.getBoundingClientRect().top <= line) {
          current = index
        }
      })

      return current
    }

    /**
     * Sticky mode: steps follow the scroll across the time the image stays
     * stuck, the last one shows a little before it unsticks.
     * @returns {number} Current step index.
     */
    stickyIndex() {
      const stickTop = parseFloat(window.getComputedStyle(this.media).top) || 0
      const scrolled = stickTop - this.container.getBoundingClientRect().top
      const range = this.container.offsetHeight - this.media.offsetHeight

      if (scrolled <= 0) {
        return 0
      }

      const progress = range > 0 ? Math.min(scrolled / range, 1) : 1

      return Math.min(Math.floor(progress * this.items.length), this.items.length - 1)
    }

    /**
     * Highlight one step, show the steps up to it and hide the ones after.
     * @param {number} currentIndex - Index of the step to highlight.
     */
    setCurrent(currentIndex) {
      this.items.forEach((item, index) => {
        item.classList.toggle('tw-timeline__item--active', index === currentIndex)
        item.classList.toggle('tw-timeline__item--seen', index <= currentIndex)
      })
    }
  }

  customElements.define('tw-timeline-progress', TwTimelineProgress)
}
